import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import Banner from "@/components/Banner";
import { UPDATE_PREFERRED_FIRST_NAME_MUTATION } from "@/features/person/queries";
import { useCanManagePeople, useCanViewInteractionsBool, useCanViewPersonInsightsBool } from "@/hooks/useCapabilities";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { NICKNAME_SUGGESTION_QUERY } from "../queries";

const dismissedKey = (pgaId: string) => `nickname-suggestion-dismissed:${pgaId}`;

function readDismissedNickname(pgaId: string): string | null {
  try {
    return sessionStorage.getItem(dismissedKey(pgaId));
  } catch {
    return null;
  }
}

/**
 * AI nickname suggestion banner. Renders between the person header and the
 * person tab bar when the person has no preferred first name and recent
 * interactions suggest one. Accepting saves via updatePerson; the shared
 * Apollo Person entity then refreshes the header name automatically.
 */
export function NicknameSuggestionBanner({ pgaId }: { pgaId: string }) {
  const canViewPersonInsights = useCanViewPersonInsightsBool();
  const canViewInteractions = useCanViewInteractionsBool();
  const canManagePeople = useCanManagePeople();
  const canSuggest = canViewPersonInsights && canViewInteractions && canManagePeople;
  const [dismissedNickname, setDismissedNickname] = useState<string | null>(() => readDismissedNickname(pgaId));
  const [accepted, setAccepted] = useState(false);

  // The person layout stays mounted when navigating between people — reset
  // per-person banner state so an accept/dismiss for A never hides B.
  useEffect(() => {
    setAccepted(false);
    setDismissedNickname(readDismissedNickname(pgaId));
  }, [pgaId]);

  const { data } = useQuery(NICKNAME_SUGGESTION_QUERY, {
    variables: { pgaId },
    skip: !pgaId || !canSuggest,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all"
  });

  const [updatePreferredFirstName] = useMutation(UPDATE_PREFERRED_FIRST_NAME_MUTATION);
  // Synchronous in-flight guard — useMutation's loading flips asynchronously,
  // so rapid double-clicks could otherwise fire duplicate saves.
  const savingRef = useRef(false);

  const informalName = data?.person?.informalName?.trim();
  const suggestedNickname = data?.insights?.suggestedNickname?.trim();

  if (!canSuggest || accepted) {
    return null;
  }
  if (!data?.person?.id || informalName || !suggestedNickname) {
    return null;
  }
  if (dismissedNickname === suggestedNickname) {
    return null;
  }

  const handleDismiss = () => {
    setDismissedNickname(suggestedNickname);
    try {
      sessionStorage.setItem(dismissedKey(pgaId), suggestedNickname);
    } catch {
      // Dismissal persistence is best-effort; the banner still hides this render.
    }
  };

  const handleAccept = async () => {
    const personId = data.person?.id;
    if (!personId || savingRef.current) {
      return;
    }
    savingRef.current = true;
    try {
      const { data: result } = await updatePreferredFirstName({
        variables: { input: { id: personId, preferredFirstName: suggestedNickname } }
      });
      if (result?.updatePerson?.success) {
        setAccepted(true);
        showSnackbar(`Preferred first name set to "${suggestedNickname}"`);
      } else {
        showErrorSnackbar(result?.updatePerson?.message || "Unable to save preferred first name");
      }
    } catch (error) {
      showErrorSnackbar((error as Error).message || "Unable to save preferred first name");
    } finally {
      savingRef.current = false;
    }
  };

  return (
    <div className="mt-4" data-testid="nickname-suggestion-banner">
      <Banner
        variant="info"
        inline
        description={`Add "${suggestedNickname}" as preferred first name?`}
        subText="Suggested from recent interactions"
        primaryAction={{ label: "Add nickname", onClick: handleAccept }}
        onClose={handleDismiss}
        testId="nickname-suggestion"
      />
    </div>
  );
}
